import { useEffect, useRef, useState } from "react";

import { recordConfirmedPurchase, type Delivery } from "./index";

export type ConfirmedPurchase = Parameters<typeof recordConfirmedPurchase>[0];
export type PurchaseRecorder = (proof: ConfirmedPurchase) => Promise<Delivery>;

/** Only the app's fresh, authoritative invoice response reaches this hook. It never reads a URL,
 * subscription or UI state. The recorder owns consent checks and persisted event-id receipts. */
export function useFirstAdPurchase(
  proof: ConfirmedPurchase | null | undefined,
  record: PurchaseRecorder = recordConfirmedPurchase,
  retryMs = 2_000,
): void {
  const [retry, setRetry] = useState(0);
  const pending = useRef<{ key: string; delivery: Promise<Delivery> } | null>(null);
  const attempts = useRef({ key: "", count: 0 });
  const eventId = proof?.event_id;
  const amount = proof?.amount_minor;
  const currency = proof?.currency;

  useEffect(() => {
    if (eventId === undefined || amount === undefined || currency === undefined) return;
    const key = JSON.stringify([eventId, amount, currency]);
    if (attempts.current.key !== key) attempts.current = { key, count: 0 };
    if (pending.current?.key !== key) {
      attempts.current.count++;
      // Preserve one in-flight call across StrictMode effect cleanup/restart.
      pending.current = { key, delivery: Promise.resolve().then(() => record({ event_id: eventId, amount_minor: amount, currency })) };
    }
    let active = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const failed = () => {
      if (!active) return;
      if (pending.current?.key === key) pending.current = null;
      if (attempts.current.count < 3) timer = setTimeout(() => setRetry((value) => value + 1), Math.max(1, retryMs));
    };
    void pending.current.delivery.then((delivery) => { if (delivery === "failed") failed(); }, failed);
    return () => { active = false; clearTimeout(timer); };
  }, [eventId, amount, currency, record, retry, retryMs]);
}
